import React, { useEffect, useState } from 'react';
import Headline from 'src/helpers/Headline/Headline';
import { LinkField, useComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextWrapper from 'src/helpers/RichTextWrapper/RichTextWrapper';
import {
  MediaPrimary,
  MediaPrimaryStaticProps,
  MediaSecondary,
  MediaSecondaryStaticProps,
} from 'src/helpers/Media';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { ContentBlockWithMediaTheme } from './ContentBlockWithMedia.theme';
import useExperienceEditor from 'lib/utils/use-experience-editor';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { cta1ToButtonProps, cta2ToButtonProps, cta3ToButtonProps } from 'src/helpers/Button';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import { useOnlineScheduling } from 'lib/forms/OnlineScheduling/OnlineSchedulingContext';

export type ContentBlockWithMediaProps =
  Feature.EnterpriseWeb.Enterprise.Components.General.ContentBlockWithMedia.ContentBlockWithMedia;

type CTALinkWrapperProps = {
  children: React.ReactNode;
  link: LinkField;
};

const CTALinkWrapper = ({ children, link }: CTALinkWrapperProps): JSX.Element =>
  link?.value?.href ? (
    <LinkWrapper
      field={link?.value}
      ariaLabel={{ value: link?.value?.text || 'CTA Link' }}
      suppressLinkText
    >
      {children}
    </LinkWrapper>
  ) : (
    <>{children}</>
  );

const ContentBlockWithMediaAW = (props: ContentBlockWithMediaProps): JSX.Element => {
  const isEE = useExperienceEditor();
  const { themeData } = useTheme(ContentBlockWithMediaTheme(props, props.fields));
  const { currentScreenWidth } = useCurrentScreenType();
  const componentProps = useComponentProps<MediaPrimaryStaticProps & MediaSecondaryStaticProps>(
    props.rendering.uid
  );
  const [inOSModal, setInOSModal] = useState(false);
  const onlineSchedulingContext = useOnlineScheduling();

  const isInOSModal = () => {
    try {
      return onlineSchedulingContext?.isInline ? true : window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    setInOSModal(isInOSModal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSchedulingContext?.isInline]);

  // Fail out if we don't have any fields
  if (!props) {
    return <></>;
  }

  const imageLayoutType =
    props.fields?.primaryImage?.value?.src &&
    !props.fields?.secondaryImage?.value?.src &&
    currentScreenWidth >= getBreakpoint('md')
      ? 'intrinsic'
      : 'responsive';

  const imageContainerWidth =
    imageLayoutType === 'intrinsic'
      ? { maxWidth: `${props.fields?.primaryImage?.value?.width}px` }
      : {};

  const focusArea = 'top center';

  const hasPrimaryVideo = props.fields?.primaryVideo;
  const hasSecondaryVideo = props.fields?.secondaryVideo;
  const hasPrimaryMedia = !!(props.fields?.primaryImage?.value?.src || hasPrimaryVideo);
  const hasSecondaryMedia = !!(props.fields?.secondaryImage?.value?.src || hasSecondaryVideo);
  const showPrimaryCaption = hasPrimaryVideo && !!props.fields?.primaryImageCaption?.value;
  const showSecondaryCaption = hasSecondaryVideo && !!props.fields?.secondaryImageCaption?.value;
  const twoImageOneCaptionMode =
    hasPrimaryMedia &&
    !!props.fields?.primaryImageCaption?.value &&
    hasSecondaryMedia &&
    !props.fields?.secondaryImageCaption?.value;

  return (
    <div
      className={
        inOSModal
          ? 'col-span-12 mt-xl grid grid-cols-12 md:gap-x-s'
          : 'col-span-12 grid grid-cols-12 md:gap-x-s'
      }
    >
      <div className={themeData.classes.headingContainer}>
        <Headline defaultTag="h2" classes={themeData.classes.headlineContainer} {...props} />
        <RichTextWrapper
          field={props.fields?.topCopy}
          className={themeData.classes.topCopyContainer}
        />
      </div>
      <div className={themeData.classes.bodyContainer}>
        <div className={themeData.classes.imageContainer}>
          <CTALinkWrapper link={props.fields?.primaryImageLink}>
            {(hasPrimaryMedia || isEE) && (
              <div
                className={
                  hasPrimaryVideo
                    ? themeData.classes.videoOuterContainer
                    : themeData.classes.imageOuterContainer
                }
                style={imageContainerWidth}
              >
                <MediaPrimary
                  {...props}
                  imageLayout={imageLayoutType}
                  focusArea={focusArea}
                  shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                  staticProps={componentProps?.mediaPrimary}
                  hideCaption={twoImageOneCaptionMode}
                />
                {showPrimaryCaption && !twoImageOneCaptionMode && (
                  <RichTextWrapper
                    classes={`${themeData.classes.captionContainer} ${
                      props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
                    }`}
                    field={props.fields?.primaryImageCaption}
                  />
                )}
              </div>
            )}
          </CTALinkWrapper>
          <CTALinkWrapper link={props.fields?.secondaryImageLink}>
            {(hasSecondaryMedia || isEE) && (
              <div
                className={
                  hasSecondaryVideo
                    ? themeData.classes.videoOuterContainer
                    : themeData.classes.imageOuterContainer
                }
                style={imageContainerWidth}
              >
                <MediaSecondary
                  {...props}
                  imageLayout={imageLayoutType}
                  focusArea={focusArea}
                  staticProps={componentProps?.mediaSecondary}
                  shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                  hideCaption={twoImageOneCaptionMode}
                />
                {showSecondaryCaption && (
                  <RichTextWrapper
                    classes={themeData.classes.captionContainer}
                    field={props.fields?.secondaryImageCaption}
                  />
                )}
              </div>
            )}
          </CTALinkWrapper>
        </div>
        {twoImageOneCaptionMode && (
          <RichTextWrapper
            classes={`${themeData.classes.captionContainer} ${
              props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
            }`}
            field={props.fields?.primaryImageCaption}
          />
        )}
        <RichTextWrapper
          classes={themeData.classes.bodyContainer}
          field={props.fields?.bottomCopy}
        />
      </div>
      <div className={themeData.classes.contentWrapper}>
        <ButtonGroup
          cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
          cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
          cta3={cta3ToButtonProps(props, themeData.classes.buttonGroupClass.cta3Classes)}
          wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
          ctaAlignment={props.fields?.ctaAlignment}
        />
      </div>
    </div>
  );
};
export default ContentBlockWithMediaAW;
